import { useLessonRating } from "@app/shared/hooks/use-lesson-rating.hook.ts";
import { URLS } from "@core/shared/utils";
import { SubmitButton } from "@design-system/components/Button";
import { RatingInput } from "@design-system/components/Form/RatingInput";
import { trans } from "@mongez/localization";
import { Form } from "@mongez/react-form";
import { Link } from "@remix-run/react";
import { LockKeyhole, LogIn, ShoppingBag, Star } from "lucide-react";

export function LessonRatingCard({ lessonId }: { lessonId: number }) {
  const { canRate, isLoggedIn, loading, rating, submitRating } =
    useLessonRating(lessonId);

  return (
    <section className="mb-6 overflow-hidden rounded-3xl border border-amber-200/80 bg-gradient-to-br from-amber-50 via-white to-orange-50 shadow-sm dark:border-amber-900/60 dark:from-amber-950/30 dark:via-slate-950 dark:to-orange-950/20">
      <div className="grid gap-5 p-5 sm:p-6 lg:grid-cols-[minmax(0,1fr)_minmax(20rem,1.2fr)] lg:items-center">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-amber-400 text-white shadow-lg shadow-amber-200 dark:shadow-none">
            <Star className="h-7 w-7 fill-current" />
          </div>
          <div>
            <p className="font-black text-slate-950 dark:text-white">
              {trans("ratings.lessonRating")}
            </p>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              {loading
                ? trans("loading")
                : trans("ratings.summary", {
                    average: rating?.average || 0,
                    total: rating?.total || 0,
                  })}
            </p>
          </div>
        </div>

        {!loading && canRate && (
          <Form
            key={rating?.currentRating || 0}
            onSubmit={submitRating}
            className="flex flex-wrap items-end justify-between gap-4 rounded-2xl border border-amber-200 bg-white/80 p-4 dark:border-amber-900 dark:bg-slate-900/70"
          >
            <RatingInput
              name="value"
              required
              size={30}
              defaultValue={rating?.currentRating}
              label={trans("ratings.yourRating")}
              hint={trans("ratings.ratingHint")}
            />
            <SubmitButton className="rounded-full px-5">
              {rating?.currentRating
                ? trans("ratings.updateRating")
                : trans("ratings.submitRating")}
            </SubmitButton>
          </Form>
        )}

        {!loading && !isLoggedIn && (
          <Link
            to={URLS.auth.login}
            className="flex items-center gap-3 rounded-2xl border border-dashed border-amber-300 bg-white/70 p-4 text-sm font-bold text-slate-700 transition hover:border-primary hover:text-primary dark:border-amber-900 dark:bg-slate-900/60 dark:text-slate-200"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              <LogIn className="h-5 w-5" />
            </span>
            <span>{trans("ratings.loginToRate")}</span>
          </Link>
        )}

        {!loading && isLoggedIn && !canRate && (
          <div className="flex items-start gap-3 rounded-2xl border border-dashed border-amber-300 bg-white/70 p-4 dark:border-amber-900 dark:bg-slate-900/60">
            <span className="relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
              <ShoppingBag className="h-5 w-5" />
              <LockKeyhole className="absolute -bottom-1 -end-1 h-4 w-4 rounded-full bg-white p-0.5 dark:bg-slate-900" />
            </span>
            <div>
              <p className="text-sm font-black text-slate-900 dark:text-white">
                {trans("ratings.purchaseRequiredTitle")}
              </p>
              <p className="mt-1 text-xs leading-5 text-slate-500 dark:text-slate-400">
                {trans("ratings.purchaseRequiredDescription")}
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
